import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Building2, Package, LogIn } from 'lucide-react';
import { isLoginCompanySelection, type ILoginCompanyChoice } from '@carrierbridge/shared';
import { useLogin } from '@/hooks/useAuth';
import { Button } from '@/components/ui/Button';
import { apiErrorMessage } from '@/lib/api-errors';
import { Input } from '@/components/ui/Input';

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  // Destination capturée par AuthProvider avant la redirection vers /login :
  // un lien partagé (session expirée) ramène à la page demandée, pas à l'accueil.
  const from =
    (location.state as { from?: { pathname?: string; search?: string } } | null)?.from ?? null;
  const returnTo = from?.pathname ? `${from.pathname}${from.search ?? ''}` : '/';
  const loginMutation = useLogin();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  // Organisations proposées quand le mot de passe est bon mais que la personne a
  // un compte dans plusieurs sociétés : aucune session n'existe tant qu'elle n'a
  // pas choisi.
  const [companies, setCompanies] = useState<ILoginCompanyChoice[] | null>(null);
  const chooserTitle = useRef<HTMLHeadingElement>(null);

  // Le formulaire disparaît au profit de la liste : sans cela le focus retombe sur <body>.
  useEffect(() => {
    if (companies) chooserTitle.current?.focus();
  }, [companies]);

  function submit(companyId?: string) {
    setError('');
    loginMutation.mutate(
      { email, password, companyId },
      {
        onSuccess: (result) => {
          if (isLoginCompanySelection(result)) setCompanies(result.companies);
          else navigate(returnTo, { replace: true });
        },
        onError: (err) => setError(apiErrorMessage(err, t('auth.login.invalidCredentials')) ?? t('auth.login.invalidCredentials')),
      },
    );
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    submit();
  }

  function backToCredentials() {
    setCompanies(null);
    setPassword('');
    setError('');
    loginMutation.reset();
  }

  const pendingCompanyId = loginMutation.isPending ? loginMutation.variables?.companyId : undefined;

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg">
      <div className="w-full max-w-[400px] px-6">
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <div className="flex h-12 w-12 items-center justify-center rounded bg-gradient-to-br from-accent to-accent-hover shadow-elevated mb-4">
            <Package className="h-6 w-6 text-white" />
          </div>
          <h1 className="text-xl font-semibold text-text">{t('auth.login.title')}</h1>
          <p className="text-[13px] text-subtle mt-1">{t('auth.login.subtitle')}</p>
        </div>

        {/* Login card */}
        <div className="rounded border border-border/60 bg-surface shadow-card p-6">
          {companies ? (
            <div className="space-y-4">
              <div>
                <h2
                  ref={chooserTitle}
                  tabIndex={-1}
                  className="text-[15px] font-semibold text-text focus:outline-none"
                >
                  {t('auth.login.chooseCompanyTitle')}
                </h2>
                <p className="text-[13px] text-subtle mt-1">
                  {t('auth.login.chooseCompanySubtitle', { email })}
                </p>
              </div>

              <ul className="space-y-2">
                {companies.map((company) => (
                  <li key={company.id}>
                    <Button
                      variant="secondary"
                      size="lg"
                      className="w-full justify-start"
                      loading={pendingCompanyId === company.id}
                      disabled={loginMutation.isPending}
                      onClick={() => submit(company.id)}
                    >
                      <Building2 className="h-4 w-4" />
                      {company.name}
                    </Button>
                  </li>
                ))}
              </ul>

              {error && (
                <div className="rounded border border-danger/20 bg-danger-soft px-3 py-2">
                  <p className="text-[13px] text-danger">{error}</p>
                </div>
              )}

              <Button variant="ghost" size="lg" className="w-full" onClick={backToCredentials}>
                {t('auth.login.chooseCompanyBack')}
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                label={t('auth.login.email')}
                type="email"
                autoComplete="username"
                placeholder={t('auth.login.emailPlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                // eslint-disable-next-line jsx-a11y/no-autofocus -- premier champ (e-mail) de l'écran de connexion, seule action de la page : l'autofocus est un choix produit
                autoFocus
              />
              <Input
                label={t('auth.login.password')}
                type="password"
                autoComplete="current-password"
                placeholder={t('auth.login.passwordPlaceholder')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />

              <div className="flex justify-end">
                <Link to="/forgot-password" className="inline-flex min-h-11 items-center text-[13px] text-accent hover:text-accent font-medium">
                  {t('auth.login.forgotPassword')}
                </Link>
              </div>

              {error && (
                <div className="rounded border border-danger/20 bg-danger-soft px-3 py-2">
                  <p className="text-[13px] text-danger">{error}</p>
                </div>
              )}

              <Button
                type="submit"
                className="w-full"
                size="lg"
                loading={loginMutation.isPending}
              >
                <LogIn className="h-4 w-4" />
                {t('auth.login.submit')}
              </Button>
            </form>
          )}
        </div>

        <p className="text-center text-[13px] text-subtle mt-4">
          {t('auth.login.noAccount')}{' '}
          <Link to="/register" className="text-accent hover:text-accent font-medium">
            {t('auth.login.createAccount')}
          </Link>
        </p>
      </div>
    </div>
  );
}
